import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  addCrossClientActiveEvaluation,
  nextStepKindForResult,
  resetCrossClientSelection,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedTab,
  setCrossClientResultsActiveEvaluation,
  startCrossClientExecutionPolling,
  tabToResultFilter,
} from '../../../store/crossClient/crossClientResults'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery, useLazyGetCrossClientQueryExecutionResultsQuery } from '../../../store/crossClient/crossClient'
import { useCreateCrossClientQueryEvaluationMutation } from '../../../store/crossClient/crossClientExecution'
import {
  CrossClientQueryEvaluationTypes,
  CrossClientQueryResultOrderBys,
  CrossClientQueryResultStatuses,
  type CrossClientQueryEvaluationType,
} from '../../../models/CrossClient'
import { addToast } from '../../../store/toast/toastSlice'
import { CROSS_CLIENT_FUNDS_TOAST_MESSAGE, isCrossClientFundsQuotaError } from '../utils/crossClientFunds'

export interface CrossClientEvaluationActions {
  evaluateResults: (resultIds: string[], type: CrossClientQueryEvaluationType) => Promise<boolean>
  evaluateOutstandingByNextStep: (type: CrossClientQueryEvaluationType, limit?: number) => Promise<boolean>
  evaluateSelectionSplit: (resultIds: string[]) => Promise<boolean>
}

export const useCrossClientEvaluationActions = (): CrossClientEvaluationActions => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)

  const [triggerGetAllResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const [triggerGetResultsPage] = useLazyGetCrossClientQueryExecutionResultsQuery()
  const [createEvaluation] = useCreateCrossClientQueryEvaluationMutation()

  const runEvaluation = useCallback(
    async (resultIds: string[], type: CrossClientQueryEvaluationType): Promise<boolean> => {
      if (!execution?.id || !resultIds.length) {
        return false
      }
      try {
        const evaluation = await createEvaluation({ accountId, executionId: execution.id, type, resultIds }).unwrap()
        dispatch(setCrossClientResultsActiveEvaluation({ resultIds, evaluationId: evaluation.id }))
        dispatch(addCrossClientActiveEvaluation(evaluation))
        dispatch(startCrossClientExecutionPolling())
        return true
      } catch (e) {
        if (isCrossClientFundsQuotaError(e)) {
          dispatch(addToast({ variant: 'error', title: 'Not enough credits', messages: [CROSS_CLIENT_FUNDS_TOAST_MESSAGE] }))
        } else {
          console.error(e)
        }
        return false
      }
    },
    [accountId, execution, createEvaluation, dispatch],
  )

  const evaluateResults = useCallback(
    async (resultIds: string[], type: CrossClientQueryEvaluationType): Promise<boolean> => {
      const success = await runEvaluation(resultIds, type)
      if (success) {
        dispatch(resetCrossClientSelection())
      }
      return success
    },
    [runEvaluation, dispatch],
  )

  const collectOutstandingPaged = useCallback(
    async (type: CrossClientQueryEvaluationType, limit: number, executionId: string): Promise<string[]> => {
      const { match } = tabToResultFilter(selectedTab)
      const ids: string[] = []
      let page = 0
      while (ids.length < limit) {
        const response = await triggerGetResultsPage({
          accountId,
          executionId,
          match,
          status: CrossClientQueryResultStatuses.INITIALIZED,
          orderBy: CrossClientQueryResultOrderBys.LOCAL_RESOLVER_SCORE,
          page,
          size: limit,
        }).unwrap()
        for (const result of response.data) {
          if (ids.length >= limit) {
            break
          }
          if (!result.activeEvaluationId && nextStepKindForResult(result, execution) === type) {
            ids.push(result.id)
          }
        }
        page += 1
        if (page >= response.page.totalPages) {
          break
        }
      }
      return ids
    },
    [accountId, execution, selectedTab, triggerGetResultsPage],
  )

  const evaluateOutstandingByNextStep = useCallback(
    async (type: CrossClientQueryEvaluationType, limit?: number): Promise<boolean> => {
      if (!execution?.id) {
        return false
      }
      try {
        if (limit) {
          const ids = await collectOutstandingPaged(type, limit, execution.id)
          return await evaluateResults(ids, type)
        }
        const { match } = tabToResultFilter(selectedTab)
        const response = await triggerGetAllResults({
          accountId,
          executionId: execution.id,
          match,
          status: CrossClientQueryResultStatuses.INITIALIZED,
          orderBy: CrossClientQueryResultOrderBys.NEXT_STEP,
        }).unwrap()
        const ids = response.data
          .filter((result) => !result.activeEvaluationId)
          .filter((result) => nextStepKindForResult(result, execution) === type)
          .map((result) => result.id)
        return await evaluateResults(ids, type)
      } catch (e) {
        console.error(e)
        return false
      }
    },
    [accountId, execution, selectedTab, triggerGetAllResults, collectOutstandingPaged, evaluateResults],
  )

  const evaluateSelectionSplit = useCallback(
    async (resultIds: string[]): Promise<boolean> => {
      if (!execution?.id || !resultIds.length) {
        return false
      }
      try {
        const response = await triggerGetAllResults({ accountId, executionId: execution.id, resultIds }).unwrap()
        const localIds: string[] = []
        const aiIds: string[] = []
        for (const result of response.data) {
          if (result.activeEvaluationId) {
            continue
          }
          const kind = nextStepKindForResult(result, execution)
          if (kind === CrossClientQueryEvaluationTypes.LOCAL_RESOLVER) {
            localIds.push(result.id)
          } else if (kind === CrossClientQueryEvaluationTypes.AI) {
            aiIds.push(result.id)
          }
        }
        let started = false
        if (localIds.length) {
          started = (await runEvaluation(localIds, CrossClientQueryEvaluationTypes.LOCAL_RESOLVER)) || started
        }
        if (aiIds.length) {
          started = (await runEvaluation(aiIds, CrossClientQueryEvaluationTypes.AI)) || started
        }
        if (started) {
          dispatch(resetCrossClientSelection())
        }
        return started
      } catch (e) {
        console.error(e)
        return false
      }
    },
    [accountId, execution, triggerGetAllResults, runEvaluation, dispatch],
  )

  return { evaluateResults, evaluateOutstandingByNextStep, evaluateSelectionSplit }
}
